import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CosmicBackground } from "../components/CosmicBackground";
import { Button } from "../components/ui/button";
import { Sparkles, Check, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "quester_monthly",
    name: "Quester",
    cadence: "Monthly",
    price: "$11",
    suffix: "/month",
    perks: ["All 8 deeper quests", "Compatibility engine", "Astrocartography map", "Cancel any time"],
  },
  {
    id: "quester_annual",
    name: "Quester",
    cadence: "Annual",
    price: "$97",
    suffix: "/year",
    perks: ["Everything in monthly", "Save $35 vs monthly", "Priority support"],
    featured: true,
  },
  {
    id: "deep_reading",
    name: "Deep Reading",
    cadence: "One-time",
    price: "$333",
    suffix: " one-time",
    perks: ["Full Human Design report", "Vedic + Western synthesis", "Personalised life-path narrative"],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const buy = async (id) => {
    setLoading(id);
    try {
      const { data } = await api.post("/checkout", {
        package_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <CosmicBackground>
      <header className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-[#94A3B8] hover:text-white text-sm flex items-center gap-1"
          data-testid="pricing-back-btn"
        >
          <ArrowLeft size={14} /> Dashboard
        </button>
        <span className="font-serif-mystic text-xl tracking-wide">AuraQuest</span>
      </header>
      <main className="max-w-6xl mx-auto px-6 md:px-12 pb-24">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Become a Quester</p>
          <h1 className="font-serif-mystic text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Choose your <span className="gold-shimmer">path</span>
          </h1>
          <p className="text-[#94A3B8] mt-3 max-w-xl mx-auto">
            All paid quests, the Compatibility Engine, and your Astrocartography map await.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl border p-7 bg-[#111625]/80 backdrop-blur-xl ${
                p.featured ? "border-[#D4AF37]/60 shadow-[0_0_40px_rgba(212,175,55,0.2)]" : "border-white/10"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#05070A] text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 rounded-full">
                  Best value
                </span>
              )}
              <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{p.cadence}</p>
              <h3 className="font-serif-mystic text-3xl mt-1">{p.name}</h3>
              <p className="mt-3">
                <span className="font-serif-mystic text-5xl">{p.price}</span>
                <span className="text-[#94A3B8] ml-1">{p.suffix}</span>
              </p>
              <ul className="mt-6 space-y-2">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                    <Check size={14} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buy(p.id)}
                disabled={loading === p.id}
                data-testid={`buy-btn-${p.id}`}
                className="mt-6 w-full bg-[#D4AF37] text-[#05070A] hover:bg-[#F3E5AB]"
              >
                {loading === p.id ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" strokeWidth={2} />}
                {p.id === "deep_reading" ? "Begin reading" : "Start subscription"}
              </Button>
            </div>
          ))}
        </div>
      </main>
    </CosmicBackground>
  );
}
